import { Product } from '../types';

// Common misspellings and domain synomym dictionary
const MISSPELLING_MAP: Record<string, string> = {
  passprot: 'passport',
  pasport: 'passport',
  passprt: 'passport',
  us: 'usa',
  america: 'usa',
  united: 'usa',
  certifcate: 'certificate',
  certificte: 'certificate',
  cert: 'certificate',
  statment: 'statement',
  stat: 'statement',
  driv: 'driving',
  drivin: 'driving',
  licence: 'license',
  licens: 'license',
  documnt: 'document',
  doc: 'docx',
  vectr: 'vector',
  templet: 'template',
  templeate: 'template',
  psd: 'psd',
  ai: 'ai',
  cdr: 'cdr',
};

// Calculate Levenshtein distance for fuzzy matching
function levenshteinDistance(a: string, b: string): number {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix: number[][] = [];

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[b.length][a.length];
}

export interface SearchResult {
  product: Product;
  score: number;
  matchedFields: string[];
}

export interface SearchResponse {
  query: string;
  normalizedQuery: string;
  results: Product[];
  totalMatches: number;
  executionTimeMs: number;
  suggestedKeywords: string[];
}

export function performSmartSearch(
  products: Product[],
  rawQuery: string,
  options?: {
    category?: string;
    country?: string;
    format?: string;
    minPrice?: number;
    maxPrice?: number;
    minRating?: number;
    sortBy?: 'relevance' | 'latest' | 'popular' | 'price-low' | 'price-high' | 'rating';
  }
): SearchResponse {
  const startTime = performance.now();
  const query = rawQuery.trim();

  const activeProducts = products.filter(p => p && (p as any).isDeleted !== true && p.status !== 'deleted');

  if (!query && !options?.category && !options?.country && !options?.format) {
    let sorted = [...activeProducts];
    if (options?.sortBy === 'price-low') sorted.sort((a, b) => a.price - b.price);
    else if (options?.sortBy === 'price-high') sorted.sort((a, b) => b.price - a.price);
    else if (options?.sortBy === 'rating') sorted.sort((a, b) => b.rating - a.rating);
    else if (options?.sortBy === 'latest') {
      sorted.sort((a, b) => {
        const timeA = Math.max(new Date(a.updatedAt || 0).getTime() || 0, new Date(a.createdAt || 0).getTime() || 0);
        const timeB = Math.max(new Date(b.updatedAt || 0).getTime() || 0, new Date(b.createdAt || 0).getTime() || 0);
        return timeB - timeA;
      });
    }
    else sorted.sort((a, b) => (b.salesCount || 0) - (a.salesCount || 0));

    const endTime = performance.now();
    return {
      query: '',
      normalizedQuery: '',
      results: sorted,
      totalMatches: sorted.length,
      executionTimeMs: Number((endTime - startTime).toFixed(2)),
      suggestedKeywords: ['Passport PSD', 'USA ID Card', 'Bank Statement', 'Gold Certificate', 'Driving License'],
    };
  }

  // Tokenize and normalize query words
  const rawTokens = query.toLowerCase().split(/\s+/).filter(Boolean);
  const normalizedTokens = rawTokens.map(token => MISSPELLING_MAP[token] || token);
  const normalizedQuery = normalizedTokens.join(' ');

  const scoredResults: SearchResult[] = [];

  for (const product of activeProducts) {
    if (!product || (product as any).isDeleted === true || product.status === 'deleted') continue;
    let score = 0;
    const matchedFields: string[] = [];

    const titleLower = product.title.toLowerCase();
    const descLower = product.description.toLowerCase();
    const catLower = product.category.toLowerCase();
    const countryLower = (product.country || '').toLowerCase();
    const tagsLower = (product.tags || []).map(t => t.toLowerCase());
    const formatsLower = (product.formats || []).map(f => f.toLowerCase());
    const softwareLower = (product.specs?.softwareRequired || []).map(s => s.toLowerCase());

    // 1. Exact Full Query Match Boosts
    if (query && titleLower === query.toLowerCase()) {
      score += 500;
      matchedFields.push('exact_title');
    } else if (query && titleLower.includes(query.toLowerCase())) {
      score += 250;
      matchedFields.push('title_contains');
    }

    if (query && countryLower && countryLower === query.toLowerCase()) {
      score += 300;
      matchedFields.push('exact_country');
    }

    if (query && catLower === query.toLowerCase()) {
      score += 300;
      matchedFields.push('exact_category');
    }

    // 2. Token Matching & Fuzzy Scoring
    let tokensMatchedCount = 0;

    for (const token of normalizedTokens) {
      let tokenScore = 0;

      // Check Title
      if (titleLower.includes(token)) {
        tokenScore += 80;
        matchedFields.push('title');
      }

      // Check Country
      if (countryLower && (countryLower.includes(token) || token.includes(countryLower))) {
        tokenScore += 90;
        matchedFields.push('country');
      }

      // Check Category
      if (catLower.includes(token)) {
        tokenScore += 70;
        matchedFields.push('category');
      }

      // Check Formats / Software
      if (formatsLower.includes(token) || softwareLower.some(s => s.includes(token))) {
        tokenScore += 60;
        matchedFields.push('format');
      }

      // Check Tags
      if (tagsLower.some(tag => tag.includes(token))) {
        tokenScore += 50;
        matchedFields.push('tags');
      }

      // Check Description
      if (descLower.includes(token)) {
        tokenScore += 25;
        matchedFields.push('description');
      }

      // Fuzzy matching if token didn't get an exact hit and is >= 4 chars
      if (tokenScore === 0 && token.length >= 4) {
        const wordsInTitle = titleLower.split(/\s+/);
        for (const word of wordsInTitle) {
          if (word.length >= 4 && Math.abs(word.length - token.length) <= 2) {
            const dist = levenshteinDistance(token, word);
            if (dist <= 2) {
              tokenScore += 35;
              matchedFields.push('fuzzy_title');
              break;
            }
          }
        }
      }

      if (tokenScore > 0) {
        score += tokenScore;
        tokensMatchedCount++;
      }
    }

    // Bonus if ALL tokens match
    if (normalizedTokens.length > 1 && tokensMatchedCount === normalizedTokens.length) {
      score += 150;
    }

    // Popularity & Rating micro-boost for tie-breaking
    score += (product.rating || 0) * 2;
    if (product.isPopular) score += 10;
    if (product.isTrending) score += 10;

    // Filter Constraints
    let passesFilters = true;

    if (query && score <= 5) {
      passesFilters = false;
    }

    if (options?.category && options.category !== 'All') {
      if (product.category.toLowerCase() !== options.category.toLowerCase()) {
        passesFilters = false;
      }
    }

    if (options?.country && options.country !== 'All') {
      if (!product.country || product.country.toLowerCase() !== options.country.toLowerCase()) {
        passesFilters = false;
      }
    }

    if (options?.format && options.format !== 'All') {
      if (!product.formats.includes(options.format as any)) {
        passesFilters = false;
      }
    }

    if (options?.minPrice !== undefined && product.price < options.minPrice) {
      passesFilters = false;
    }

    if (options?.maxPrice !== undefined && product.price > options.maxPrice) {
      passesFilters = false;
    }

    if (options?.minRating !== undefined && product.rating < options.minRating) {
      passesFilters = false;
    }

    if (passesFilters) {
      scoredResults.push({ product, score, matchedFields });
    }
  }

  // Sort Results
  if (options?.sortBy === 'price-low') {
    scoredResults.sort((a, b) => a.product.price - b.product.price);
  } else if (options?.sortBy === 'price-high') {
    scoredResults.sort((a, b) => b.product.price - a.product.price);
  } else if (options?.sortBy === 'rating') {
    scoredResults.sort((a, b) => b.product.rating - a.product.rating);
  } else if (options?.sortBy === 'latest') {
    scoredResults.sort((a, b) => {
      const tA1 = a.product.updatedAt ? new Date(a.product.updatedAt).getTime() : NaN;
      const tA2 = a.product.createdAt ? new Date(a.product.createdAt).getTime() : NaN;
      const tB1 = b.product.updatedAt ? new Date(b.product.updatedAt).getTime() : NaN;
      const tB2 = b.product.createdAt ? new Date(b.product.createdAt).getTime() : NaN;
      const vA = !isNaN(tA1) ? tA1 : (!isNaN(tA2) ? tA2 : 0);
      const vB = !isNaN(tB1) ? tB1 : (!isNaN(tB2) ? tB2 : 0);
      return vB - vA;
    });
  } else if (options?.sortBy === 'popular') {
    scoredResults.sort((a, b) => (b.product.salesCount || 0) - (a.product.salesCount || 0));
  } else {
    // Default: Sort by relevance score descending
    scoredResults.sort((a, b) => b.score - a.score);
  }

  const finalProducts = scoredResults.map(r => r.product);
  const endTime = performance.now();

  // Generate intelligent suggestions
  const suggestedKeywords = [
    'USA Passport PSD',
    'UK ID Card Template',
    'California Driver License',
    'Chase Bank Statement PSD',
    'Gold Seal Award Certificate',
    'Utility Bill Editable Word',
    'Biometric Vector Smart Layer'
  ].filter(k => k.toLowerCase() !== query.toLowerCase());

  return {
    query,
    normalizedQuery,
    results: finalProducts,
    totalMatches: finalProducts.length,
    executionTimeMs: Number((endTime - startTime).toFixed(2)),
    suggestedKeywords,
  };
}

// Local Storage Manager for Recent Searches
export function getRecentSearches(): string[] {
  try {
    const saved = localStorage.getItem('rh_recent_searches');
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
}

export function saveRecentSearch(term: string): void {
  const clean = term.trim();
  if (!clean) return;
  try {
    let recent = getRecentSearches();
    recent = recent.filter(item => item.toLowerCase() !== clean.toLowerCase());
    recent.unshift(clean);
    recent = recent.slice(0, 10); // Keep top 10
    localStorage.setItem('rh_recent_searches', JSON.stringify(recent));
  } catch (e) {
    console.warn('Failed to save recent search', e);
  }
}

export function removeRecentSearch(term: string): string[] {
  try {
    let recent = getRecentSearches();
    recent = recent.filter(item => item.toLowerCase() !== term.toLowerCase());
    localStorage.setItem('rh_recent_searches', JSON.stringify(recent));
    return recent;
  } catch {
    return [];
  }
}

export function clearAllRecentSearches(): void {
  try {
    localStorage.removeItem('rh_recent_searches');
  } catch (e) {
    console.warn(e);
  }
}
